
import {of as observableOf,  Observable } from 'rxjs';

import {catchError} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AttendeesFilterModel, AttendeesPage, AttendeesPagedData, IAttendeeRegistrationDetails } from '@routes/events/models/attendeelist-model';
import { IAttendee, IAttendeeBatch, IAttendeeDetail, IAttendeeDetails, IAttendeeExportDetails,
     IAttendeeMailing, ICheckinResponse, IFrontdeskAttendee, IScanRequest } from '@shared/services/api/legacy/attendees-service/attendees-model';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/legacy/response';
import { FrontdeskPage, FrontdeskPagedData } from '@routes/frontdesk/models/frontdesk-model';

@Injectable()
export class AttendeesService {
    constructor(private http: HttpClient) { }

    private static get URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${AttendeesService.ENDPOINT}`; }
    private static readonly ENDPOINT = '/events/{eventId}/attendees';

    public getAttendee(eventId: string, attendeeId: string): Observable<SuccessResponse<IAttendeeDetail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IAttendeeDetail>>(AttendeesService.URL.replace('{eventId}', eventId) + '/' + attendeeId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getAttendees(eventId: string, page: AttendeesPage): Observable<SuccessResponse<AttendeesPagedData<IAttendee>> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<AttendeesPagedData<IAttendee>>>(AttendeesService.URL.replace('{eventId}', eventId), page).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getFrontdeskAttendees(eventId: string, page: FrontdeskPage): Observable<SuccessResponse<FrontdeskPagedData<IFrontdeskAttendee>> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<FrontdeskPagedData<IFrontdeskAttendee>>>(AttendeesService.URL.replace('{eventId}', eventId) + '/frontdesks', page).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getAttendeesExport(eventId: string, page: AttendeesPage): Observable<SuccessResponse<IAttendeeExportDetails> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IAttendeeExportDetails>>(AttendeesService.URL.replace('{eventId}', eventId) + '/exports', page).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getAttendeesPrint(eventId: string, page: AttendeesPage): Observable<SuccessResponse<IAttendeeDetails> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IAttendeeDetails>>(AttendeesService.URL.replace('{eventId}', eventId) + '/prints', page).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public UpdateAttendee(eventId: string, attendeeMailing: IAttendeeMailing): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL.replace('{eventId}', eventId) + '/' + attendeeMailing.attendeeId, { mailPermission: attendeeMailing.mailPermission }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public checkInAttendee(eventId: string, attendeeId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL.replace('{eventId}', eventId) + '/' + attendeeId + '/checkins', { }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public checkoutAttendee(eventId: string, attendeeId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL.replace('{eventId}', eventId) + '/' + attendeeId + '/checkouts', { }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public undoCheckInAttendee(eventId: string, attendeeId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL.replace('{eventId}', eventId) + '/' + attendeeId + '/undocheckins', { }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public scanAttendee(eventId: string, scanRequest: IScanRequest): Observable<SuccessResponse<ICheckinResponse> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<ICheckinResponse>>(AttendeesService.URL.replace('{eventId}', eventId) + '/scans', { value: scanRequest.value, isCheckout: scanRequest.isCheckout }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public noShowAttendee(eventId: string, attendeeId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL.replace('{eventId}', eventId) + '/' + attendeeId + '/noshows', { }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public checkInAttendees(eventId: string, attendees: IAttendeeBatch): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL.replace('{eventId}', eventId) + '/batchcheckins', attendees).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public checkoutAttendees(eventId: string, attendees: IAttendeeBatch): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL.replace('{eventId}', eventId) + '/batchcheckouts', attendees).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public undoCheckInAttendees(eventId: string, attendees: IAttendeeBatch): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL.replace('{eventId}', eventId) + '/batchundocheckins', attendees).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public noShowAttendees(eventId: string, attendees: IAttendeeBatch): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL.replace('{eventId}', eventId) + '/batchnoshows', attendees).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getAttendeeDetails(eventId: string, filterData: AttendeesFilterModel, isExport: boolean = false): Observable<SuccessResponse<IAttendeeRegistrationDetails> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IAttendeeRegistrationDetails>>(AttendeesService.URL.replace('{eventId}', eventId) + '/details',
             { filterModel: filterData, isExport }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }
}
